import { GlobalPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Injectable, OnDestroy, ViewContainerRef } from '@angular/core';

import { merge, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { LinkInput, OverlayPosition } from '@recruitee/prosemirror';

import { LinkInputComponent } from './link-input.component';

@Injectable()
export class LinkComponentService implements LinkInput, OnDestroy {
  private positionStrategy = new GlobalPositionStrategy();
  private overlayRef: OverlayRef | null;
  private toolbarRef: ComponentRef<LinkInputComponent> | null;
  private portalRef: ComponentPortal<LinkInputComponent> | null;

  private ngOnDestroy$ = new Subject<void>();

  constructor(private overlay: Overlay, private vcRef: ViewContainerRef) {}

  public open(
    initialValue: string | null,
    position: OverlayPosition,
    setValueCb: (val: string) => void,
    removeCb: () => void,
    exitCb: () => void,
  ) {
    this.overlayRef = this.overlay.create({
      positionStrategy: this.positionStrategy,
      hasBackdrop: true,
      backdropClass: 'blank-backdrop',
    });

    this.setPosition(position);

    this.portalRef = new ComponentPortal(LinkInputComponent, this.vcRef);
    this.toolbarRef = this.overlayRef.attach(this.portalRef);
    const { removeLink, formControl, exit } = this.toolbarRef.instance;
    const close$ = merge(this.overlayRef.backdropClick(), exit);

    formControl.setValue(initialValue);

    removeLink.pipe(take(1), takeUntil(close$)).subscribe(() => {
      removeCb();
      exitCb();
    });

    close$.pipe(take(1), takeUntil(removeLink)).subscribe(() => {
      const { value } = formControl;
      value ? setValueCb(value) : removeCb();
      exitCb();
    });

    this.toolbarRef.changeDetectorRef.detectChanges();
  }

  public close(): void {
    this.toolbarRef?.destroy();
    this.toolbarRef = null;
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.portalRef = null;
  }

  public setPosition(rect: OverlayPosition): void {
    this.positionStrategy.left(`${rect.left}px`);
    this.positionStrategy.top(`${rect.top - 48}px`);
    this.overlayRef?.updatePosition();
  }

  public ngOnDestroy() {
    this.close();
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
